import { minLength, requiredIf } from '@vuelidate/validators';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type { ComputedRef } from 'vue';
import { citizenshipEntity } from 'shared';

dayjs.extend(customParseFormat);

import { validationI18n } from '../../../entities/validation-i18n';
import { validationRulesLib } from '../../../shared/lib';
import { IIN_LENGTH, isValidIin } from './iin';

const isValidDocumentExpireDate = (value: string, departureDate?: string) => {
  if (departureDate) {
    const formattedDate = dayjs(value, 'DD.MM.YYYY', true);

    return (
      value === '' ||
      (validationRulesLib.isValidDate(value) &&
        validationRulesLib.isNotExpired(formattedDate, departureDate))
    );
  }
  return value === '' || validationRulesLib.isValidDate(value);
};

const isValidDocumentGivenAtDate = (value: string, departureDate?: string) => {
  if (departureDate) {
    const formattedDate = dayjs(value, 'DD.MM.YYYY', true);

    return (
      value === '' ||
      (validationRulesLib.isValidDate(value) &&
        validationRulesLib.isAfterOrEqual(formattedDate, departureDate))
    );
  }
  return value === '' || validationRulesLib.isValidDate(value);
};

/**
 * Base passenger validations with i18n support
 * @see https://vuelidate-next.netlify.app/advanced_usage.html#i18n-support
 */
export const getBaseValidations = (
  citizenship: ComputedRef<string>,
  departureDate: ComputedRef<string | undefined> | undefined,
  messagePath: string,
) => {
  const { required, withI18nMessage } =
    validationI18n.model.useLocalizedValidators(messagePath);
  /**
   * Need to use method since validators does not reflect reactive property change.
   * Validators call every function that is passed to it on every value update
   */
  const isIinRequired = () =>
    citizenship.value === citizenshipEntity.config.KAZAKHSTAN_CODE;

  return {
    firstName: {
      required,
      alpha: withI18nMessage(validationRulesLib.isAlphaHyphen),
    },
    lastName: {
      required,
      alpha: withI18nMessage(validationRulesLib.isAlphaHyphen),
    },
    gender: { required },
    dob: {
      required,
      isValidDob: withI18nMessage(validationRulesLib.isValidDate),
    },
    citizenship: { required },
    document: {
      number: {
        required,
        alphaNumCyrillic: withI18nMessage(
          validationRulesLib.isAlphaNumCyrillic,
        ),
        docMinLength: withI18nMessage(minLength(5)),
      },
      expiresAt: {
        isValidDocExpiration: withI18nMessage((value: string) =>
          isValidDocumentExpireDate(value, departureDate?.value),
        ),
      },
    },
    iin: {
      required: withI18nMessage(requiredIf(isIinRequired)),
      iinMinLength: withI18nMessage(
        (value: string) => !isIinRequired() || value.length === IIN_LENGTH,
      ),
      isInvalidIin: withI18nMessage(
        (value: string) => !isIinRequired() || isValidIin(value),
      ),
    },
  };
};

export const getBaseValidationsForSMProvider = (
  citizenship: ComputedRef<string>,
  departureDate: ComputedRef<string | undefined> | undefined,
  messagePath: string,
) => {
  const { required, withI18nMessage } =
    validationI18n.model.useLocalizedValidators(messagePath);
  /**
   * Need to use method since validators does not reflect reactive property change.
   * Validators call every function that is passed to it on every value update
   */
  const isFinRequired = () =>
    citizenship.value === citizenshipEntity.config.AZERBAIJAN_CODE;
  const isSeriesRequired = () =>
    !citizenshipEntity.config.countriesWithoutSeries.includes(
      citizenship.value,
    );

  return {
    document: {
      expiresAt: {
        required,
        isValidDocExpiration: withI18nMessage((value: string) =>
          isValidDocumentExpireDate(value, departureDate?.value),
        ),
      },
      givenAt: {
        required,
        isValidDocGivenAt: withI18nMessage((value: string) =>
          isValidDocumentGivenAtDate(value, departureDate?.value),
        ),
      },
      organization: {
        required,
        alpha: withI18nMessage(validationRulesLib.isAlphaNumSpace),
      },
      series: {
        required: withI18nMessage(requiredIf(isSeriesRequired)),
        isValidSeries: withI18nMessage(
          (value: string) =>
            !isSeriesRequired() ||
            validationRulesLib.isValidDocumentSeries(value),
        ),
      },
    },
    fin: {
      required: withI18nMessage(requiredIf(isFinRequired)),
      finMinLength: withI18nMessage(
        (value: string) => !isFinRequired() || value.length === 7,
      ),
    },
  };
};
